import React, { useRef, useState } from 'react';
import Hide from '@uber/base-icons/hide';
import Lock from '@uber/base-icons/lock';
import TrashCan from '@uber/base-icons/trash-can';
import { useSnackbar } from 'baseui/snackbar';
import { ACTION_BY } from '../../../../common/constant/constant';
import type { DeleteAnnotationPayloadT } from '../../../../common/side-panel/analytics/types';
import useAnalyticsForSidePanel from '../../../../common/side-panel/analytics/useAnalyticsForSidePanel';
import type { AnnotationInfoT } from '../../annotations/types';
import { SHORT_TOAST_DURATION, SIDE_PANEL_ACTIONS } from '../../const';
import { useDispatch } from '../../hooks/use-dispatch';
import { useSelectedAnnotationLabel } from '../../hooks/use-selected-annotation-label';
import { useSidePanel } from '../../side-panel-provider';
import type { DeleteAnnotationModalContentComponentProps } from '../../types';
type DeleteModalPropsT = {
  isOpen: false;
} | ({
  isOpen: true;
} & DeleteAnnotationModalContentComponentProps);
type PromiseResolverT = {
  resolve: (value?: unknown) => void;
  reject: () => void;
};
export function useDeleteHandler() {
  const selectedAnnotationLabel = useSelectedAnnotationLabel();
  const {
    sendEventForDeleteAnnotation
  } = useAnalyticsForSidePanel();
  const {
    enqueue
  } = useSnackbar();
  const dispatch = useDispatch();
  const {
    deleteAnnotationModalContentComponent
  } = useSidePanel();
  const [deleteModalProps, setDeleteModalProps] = useState<DeleteModalPropsT>({
    isOpen: false
  });
  const deleteModalPromiseRef = useRef<PromiseResolverT | null>(null);
  const shouldShowDeleteModal = !!deleteAnnotationModalContentComponent;
  const handleDeleteModalCancel = () => {
    setDeleteModalProps({
      isOpen: false
    });
    deleteModalPromiseRef.current?.reject();
    deleteModalPromiseRef.current = null;
  };
  const handleDeleteModalSubmit = () => {
    setDeleteModalProps({
      isOpen: false
    });
    deleteModalPromiseRef.current?.resolve();
    deleteModalPromiseRef.current = null;
  };
  const showDeleteModal = (annotation: AnnotationInfoT) => {
    if (!shouldShowDeleteModal) {
      dispatch({
        type: SIDE_PANEL_ACTIONS.DELETE_ANNOTATION,
        annotationIdx: annotation.id
      });
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      deleteModalPromiseRef.current = {
        resolve,
        reject
      };
      setDeleteModalProps({
        isOpen: true,
        annotationId: annotation.id,
        onCancel: handleDeleteModalCancel,
        onSubmit: handleDeleteModalSubmit
      });
    });
  };
  const emitEvent = (annotation: AnnotationInfoT) => {
    const eventPayload: DeleteAnnotationPayloadT = {
      objectType: SIDE_PANEL_ACTIONS.DELETE_ANNOTATION,
      label: selectedAnnotationLabel?.title ?? '',
      objectId: annotation.id,
      actionBy: ACTION_BY.EDIT
    };
    sendEventForDeleteAnnotation(eventPayload);
  };
  const showDeleteDisabledSnackbar = (annotation: AnnotationInfoT) => {
    const {
      isLocked
    } = annotation;
    enqueue({
      message: `Annotation is ${isLocked ? 'locked' : 'hidden'} and can't be deleted.`,
      startEnhancer: () => isLocked ? React.createElement(Lock, {
        title: 'Locked'
      }) : React.createElement(Hide, {
        title: 'Hidden'
      })
    }, SHORT_TOAST_DURATION);
  };
  const showDeleteSuccessSnackbar = () => {
    enqueue({
      message: 'Annotation deleted successfully.',
      startEnhancer: () => React.createElement(TrashCan, {
        title: 'Deleted'
      })
    }, SHORT_TOAST_DURATION);
  };
  const handleDeleteAnnotation = async (annotation: AnnotationInfoT) => {
    const {
      isLocked,
      isVisible
    } = annotation;
    if (isLocked || !isVisible) {
      showDeleteDisabledSnackbar(annotation);
      return;
    }
    try {
      await showDeleteModal(annotation);
    } catch (error) {
      return;
    }
    emitEvent(annotation);
    showDeleteSuccessSnackbar();
  };
  return {
    handleDeleteAnnotation,
    deleteModalProps
  };
}